import * as Yup from 'yup';

export const LEAD_SCHEMA = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().nullable(),
  companyId: Yup.string().nullable().default(null),
  companyName: Yup.string().nullable().default(null),
  emailAddress: Yup.string()
    .email('Must be a valid email')
    .required('Required'),
  businessNumber: Yup.string().nullable(),
  mobileNumber: Yup.string().nullable(),
  other: Yup.string(),
  website: Yup.string(),
  nameToPrintOnCheck: Yup.string(),
  isSubCustomer: Yup.bool(),
  streetAddress1: Yup.string(),
  streetAddress2: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zipcode: Yup.string(),
  country: Yup.string(),
  tagId: Yup.array(Yup.string()),
  data: Yup.array(
    Yup.object().shape({
      dataId: Yup.string(),
      value: Yup.mixed().nullable(),
      error: Yup.string().optional(),
    }),
  ).nullable(),
  accountStatusId: Yup.string().nullable(),
});

export const FILE_SCHEMA = Yup.object({
  id: Yup.string().required('Required'),
  source: Yup.string().required('Required'),
  referenceId: Yup.string().required('Required'),
  fileType: Yup.string().required('Required'),
  fileName: Yup.string().required('Required'),
  fileUrl: Yup.string().required('Required'),
  fileThumbnail: Yup.string().optional(),
  fileSize: Yup.number().required('Required'),
});

export const UPLOAD_ATTACHMENTS_SCHEMA = Yup.object({
  files: Yup.array(FILE_SCHEMA).required(),
});

export type UPLOAD_ATTACHMENTS_FORM = Yup.InferType<
  typeof UPLOAD_ATTACHMENTS_SCHEMA
>;

export type LEAD_FORM = Yup.InferType<typeof LEAD_SCHEMA>;

export const SIMPLE_LEAD_SCHEMA = LEAD_SCHEMA.pick([
  'firstName',
  'lastName',
  'mobileNumber',
  'emailAddress',
  'tagId',
]);

export type SIMPLE_LEAD_FORM = Yup.InferType<typeof SIMPLE_LEAD_SCHEMA>;

export const MULTI_LEADS_EDIT_SCHEMA = LEAD_SCHEMA.pick(['tagId']);

export type MULTI_LEADS_EDIT_FORM = Yup.InferType<
  typeof MULTI_LEADS_EDIT_SCHEMA
>;

export const COMPANY_SCHEMA = Yup.object().shape({
  name: Yup.string().required('Required'),
  industry: Yup.string().nullable(),
  type: Yup.string().nullable(),
  city: Yup.string().nullable(),
  state: Yup.string().nullable(),
  postalCode: Yup.string().nullable(),
  noEmployees: Yup.number().nullable(),
  annualRevenue: Yup.number().nullable(),
  timezone: Yup.string(),
  description: Yup.string(),
  linkedinUrl: Yup.string().optional().nullable(),
  companyWebsite: Yup.string().optional().nullable(),
  photoUrl: Yup.string().optional().nullable(),
  tagId: Yup.array(Yup.string()).optional().nullable(),
  data: Yup.array(
    Yup.object().shape({
      dataId: Yup.string(),
      value: Yup.mixed().nullable(),
      error: Yup.string().optional(),
    }),
  ).nullable(),
});

export type COMPANY_FORM = Yup.InferType<typeof COMPANY_SCHEMA>;

export const SIMPLE_COMPANY_SCHEMA = COMPANY_SCHEMA.pick([
  'name',
  'industry',
  'type',
  'tagId',
]);

export type SIMPLE_COMPANY_FORM = Yup.InferType<typeof SIMPLE_COMPANY_SCHEMA>;

export const MULTI_COMPANIES_EDIT_SCHEMA = LEAD_SCHEMA.pick(['tagId']);

export type MULTI_COMPANIES_EDIT_FORM = Yup.InferType<
  typeof MULTI_COMPANIES_EDIT_SCHEMA
>;

export const DEAL_SCHEMA = Yup.object().shape({
  dealId: Yup.array().of(Yup.string().required()).required('Required'),
});

export type DEAL_FORM = Yup.InferType<typeof DEAL_SCHEMA>;

export const LEAD_ONLY_SCHEMA = Yup.object().shape({
  leadId: Yup.array().of(Yup.string()).required('Required'),
});

export type LEAD_ONLY_FORM = Yup.InferType<typeof LEAD_ONLY_SCHEMA>;

export const ADDRESS_SCHEMA = Yup.object().shape({
  streetAddress1: Yup.string(),
  streetAddress2: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zipcode: Yup.string(),
  country: Yup.string(),
  postalCode: Yup.string(),
});

export type ADDRESS_FORM = Yup.InferType<typeof ADDRESS_SCHEMA>;

export const EMAIL_SCHEMA = Yup.object().shape({
  to: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string(),
        value: Yup.string().email('Must be a valid email address'),
      }),
    )
    .min(1)
    .required('Field is required'),
  from: Yup.string().email('Must be a valid email address'),
  cc: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string(),
        value: Yup.string().email('Must be a valid email address'),
      }),
    )
    .optional(),
  bcc: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string(),
        value: Yup.string().email('Must be a valid email address'),
      }),
    )
    .optional(),
  subject: Yup.string().required('Please provide a subject'),
  text: Yup.string(),
});

export type EMAIL_FORM = Yup.InferType<typeof EMAIL_SCHEMA>;

export const SMS_SCHEMA = Yup.object().shape({
  to: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string(),
        value: Yup.string(),
      }),
    )
    .min(1)
    .required('Field is required'),
  body: Yup.string().required('Field is required'),
});

export type SMS_FORM = Yup.InferType<typeof SMS_SCHEMA>;

export const CUSTOM_FIELDS_CONFIGURATOR_SCHEMA = Yup.object({
  selected: Yup.array(
    Yup.object({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
  ),
});

export type CUSTOM_FIELDS_CONFIGURATOR_FORM = Yup.InferType<
  typeof CUSTOM_FIELDS_CONFIGURATOR_SCHEMA
>;

export const EXPORT_DATA_CONFIGURATOR_SCHEMA = Yup.object({
  selected: Yup.array(
    Yup.object({
      label: Yup.string().required(),
      value: Yup.string().required(),
      type: Yup.string().oneOf(['key', 'dataId']),
    }),
  ),
});

export type EXPORT_DATA_CONFIGURATOR_FORM = Yup.InferType<
  typeof EXPORT_DATA_CONFIGURATOR_SCHEMA
>;

export const LOG_ACTIVITY_SCHEMA = Yup.object({
  note: Yup.string().required('Note is required'),
  specifyDateTime: Yup.boolean().default(false),
  logType: Yup.string().required('Log type is required'),
  logDate: Yup.date().when('specifyDateTime', {
    is: true,
    then: (schema) => schema.required('Log date required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export type LOG_ACTIVITY_FORM = Yup.InferType<typeof LOG_ACTIVITY_SCHEMA>;
